/**
 * Per-user regional prefs (core.user_setting 0035) + formatters bound to them.
 * useRegionalPrefs reads/writes the caller's own locale / timezone / currency;
 * useUserFormat returns money/date formatters pre-bound to those prefs (falling
 * back to EUR + the runtime locale/zone). Any surface that wants user-localised
 * output calls useUserFormat() instead of @solum/format directly.
 */
import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSolum } from './client'
import { formatDate, formatMoney, type DateOptions, type MoneyOptions } from '@solum/format'

export interface RegionalPrefs { locale?: string; timezone?: string; currency?: string }

export function useRegionalPrefs() {
  const { client } = useSolum()
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['user-settings'], queryFn: () => client.raw().rpcScalar<RegionalPrefs>('my_settings') })
  const setPref = useMutation({
    mutationFn: (p: { key: string; value: unknown }) =>
      client.raw().rpcScalar('set_user_setting', { p_key: p.key, p_value: p.value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user-settings'] }),
  })
  return { prefs: (q.data ?? {}) as RegionalPrefs, setPref, isLoading: q.isLoading }
}

export function useUserFormat() {
  const { prefs } = useRegionalPrefs()
  return useMemo(() => ({
    money: (v: unknown, opts?: MoneyOptions) =>
      formatMoney(v, { locale: prefs.locale, currency: prefs.currency ?? 'EUR', ...opts }),
    date: (v: unknown, opts?: DateOptions) =>
      formatDate(v, { locale: prefs.locale, timeZone: prefs.timezone, ...opts }),
    prefs,
  }), [prefs])
}
